import React, { useState } from "react";
import { Phone, MapPin, Mail, Instagram, MessageCircle, Send, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const PHONE = "+91 86059 88521";
const PHONE_RAW = "918605988521";
const WHATSAPP_NUMBER = "918605988521";
const EMAIL = "raunak.gadhiya@gmail.com";
const INSTA_HANDLE = "sareesbyraunak";
const ADDRESS_LINES = [
  "53, Sargam Society, Lane 1",
  "First Floor, Karvenagar",
  "Pune 411052, Maharashtra, India",
];

export default function Contact() {
  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-10 py-16" data-testid="contact-page">
      <div className="text-center mb-12">
        <div className="text-[11px] tracking-[0.3em] uppercase text-[#c9a34a] mb-3">Get in touch</div>
        <h1 className="font-serif text-4xl sm:text-5xl text-[#2b1b1c]">We'd love to hear from you</h1>
        <div className="gold-divider mt-6"><span className="text-lg">✦</span></div>
        <p className="text-[#7c7273] mt-6 max-w-xl mx-auto">
          Questions about a saree, drape styling, custom orders or bulk gifting — reach us any way you like. We usually respond the same day.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <ContactCard
          icon={<Phone size={22} className="text-[#c9a34a]" />}
          label="Call us"
          value={PHONE}
          href={`tel:+${PHONE_RAW}`}
          testId="contact-phone"
        />
        <ContactCard
          icon={<MessageCircle size={22} className="text-[#4a7a59]" />}
          label="WhatsApp"
          value={PHONE}
          href={`https://wa.me/${WHATSAPP_NUMBER}`}
          external
          testId="contact-whatsapp"
        />
        <ContactCard
          icon={<Mail size={22} className="text-[#7a1e2b]" />}
          label="Email"
          value={EMAIL}
          href={`mailto:${EMAIL}`}
          testId="contact-email"
        />
        <ContactCard
          icon={
            <span
              className="w-6 h-6 rounded flex items-center justify-center"
              style={{
                background:
                  "linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)",
              }}
            >
              <Instagram size={14} className="text-white" />
            </span>
          }
          label="Instagram"
          value={`@${INSTA_HANDLE}`}
          href={`https://www.instagram.com/${INSTA_HANDLE}/`}
          external
          testId="contact-instagram"
        />
      </div>

      {/* Address block */}
      <div className="bg-[#f5f3ef] p-8 mt-6" data-testid="contact-address">
        <div className="flex items-start gap-4">
          <MapPin size={22} className="text-[#c9a34a] mt-1 flex-shrink-0" />
          <div>
            <div className="text-[11px] uppercase tracking-[0.25em] text-[#7c7273]">Visit our studio</div>
            <div className="font-serif text-xl text-[#2b1b1c] mt-1">Sarees by Raunak</div>
            <div className="text-[#2b1b1c] mt-2 leading-relaxed">
              {ADDRESS_LINES.map((l, i) => <div key={i}>{l}</div>)}
            </div>
            <a
              href="https://www.google.com/maps/search/?api=1&query=53%2C+Sargam+Society%2C+Lane+1%2C+Karvenagar%2C+Pune+411052"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block mt-4 text-sm text-[#7a1e2b] hover:text-[#5c1520] underline underline-offset-4"
              data-testid="contact-map-link"
            >
              Open in Google Maps →
            </a>
          </div>
        </div>
      </div>

      <p className="text-center text-xs text-[#7c7273] mt-10 tracking-wider uppercase">
        Studio visits by appointment. Please WhatsApp before arriving.
      </p>

      {/* Contact form */}
      <ContactForm />
    </div>
  );
}

function ContactForm() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const on = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2) return toast.error("Please share your name");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) return toast.error("Please share a valid email");
    if (form.message.trim().length < 10) return toast.error("Please write a slightly longer message");
    setSending(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success("Thank you — we'll write back within the day.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send. Try again in a moment.");
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="mt-16 bg-[#f5f3ef] p-10 text-center" data-testid="contact-form-thanks">
        <CheckCircle2 size={44} className="text-[#4a7a59] mx-auto" />
        <h3 className="font-serif text-2xl text-[#2b1b1c] mt-4">Thank you, {form.name.split(" ")[0]}.</h3>
        <p className="text-[#7c7273] mt-2">We&rsquo;ve received your message and will write back as soon as we can — usually within a few hours.</p>
        <button
          className="btn-outline mt-6"
          onClick={() => { setSent(false); setForm({ name: "", email: "", phone: "", subject: "", message: "" }); }}
          data-testid="contact-form-reset"
        >
          Send another message
        </button>
      </div>
    );
  }

  return (
    <div className="mt-16" data-testid="contact-form-block">
      <div className="text-center mb-8">
        <div className="text-[11px] tracking-[0.3em] uppercase text-[#c9a34a] mb-2">Send a message</div>
        <h2 className="font-serif text-3xl text-[#2b1b1c]">Or write to us here</h2>
      </div>
      <form onSubmit={submit} className="bg-white border border-[#e8e4db] p-6 sm:p-8 space-y-4" data-testid="contact-form">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-[#7c7273] block mb-1.5">Your name <span className="text-[#7a1e2b]">*</span></label>
            <input
              type="text"
              value={form.name}
              onChange={on("name")}
              placeholder="e.g. Priya Sharma"
              className="w-full border border-[#e8e4db] px-3 py-2 focus:outline-none focus:border-[#7a1e2b] transition-colors"
              required
              data-testid="contact-name"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-[#7c7273] block mb-1.5">Email <span className="text-[#7a1e2b]">*</span></label>
            <input
              type="email"
              value={form.email}
              onChange={on("email")}
              placeholder="you@example.com"
              className="w-full border border-[#e8e4db] px-3 py-2 focus:outline-none focus:border-[#7a1e2b] transition-colors"
              required
              data-testid="contact-email-input"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-[#7c7273] block mb-1.5">Phone (optional)</label>
            <input
              type="tel"
              value={form.phone}
              onChange={on("phone")}
              placeholder="+91 …"
              className="w-full border border-[#e8e4db] px-3 py-2 focus:outline-none focus:border-[#7a1e2b] transition-colors"
              data-testid="contact-phone-input"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-[#7c7273] block mb-1.5">Subject</label>
            <input
              type="text"
              value={form.subject}
              onChange={on("subject")}
              placeholder="e.g. Custom Banarasi enquiry"
              className="w-full border border-[#e8e4db] px-3 py-2 focus:outline-none focus:border-[#7a1e2b] transition-colors"
              data-testid="contact-subject"
            />
          </div>
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-[#7c7273] block mb-1.5">Message <span className="text-[#7a1e2b]">*</span></label>
          <textarea
            value={form.message}
            onChange={on("message")}
            rows={5}
            placeholder="Tell us what you're looking for…"
            className="w-full border border-[#e8e4db] px-3 py-2 focus:outline-none focus:border-[#7a1e2b] transition-colors font-sans"
            required
            data-testid="contact-message"
          />
          <div className="text-[11px] text-[#7c7273] mt-1">{form.message.length}/5000</div>
        </div>
        <div className="flex items-center justify-between pt-2">
          <p className="text-[11px] text-[#7c7273]">We&rsquo;ll only use your details to reply to your enquiry.</p>
          <button
            type="submit"
            disabled={sending}
            className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="contact-submit"
          >
            <Send size={14} />
            {sending ? "Sending…" : "Send message"}
          </button>
        </div>
      </form>
    </div>
  );
}

function ContactCard({ icon, label, value, href, external, testId }) {
  const external_props = external ? { target: "_blank", rel: "noopener noreferrer" } : {};
  return (
    <a
      href={href}
      {...external_props}
      className="group bg-white border border-[#e8e4db] p-5 flex items-start gap-4 hover:border-[#7a1e2b] hover:shadow-sm transition-all"
      data-testid={testId}
    >
      <div className="flex-shrink-0 pt-0.5">{icon}</div>
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-[0.25em] text-[#7c7273]">{label}</div>
        <div className="font-serif text-lg text-[#2b1b1c] mt-1 group-hover:text-[#7a1e2b] transition-colors break-all">{value}</div>
      </div>
    </a>
  );
}
